import * as React from 'react';

import { Box, List, ListItemButton, ListItemText, ListSubheader, Collapse, Typography } from '@unity/cloud-ui';
import { styled } from '@unity/cloud-ui/styles';
import { CaretDown, CaretUp, ArrowSquareOut } from '@phosphor-icons/react';

export interface HeaderMenuLinksMobileProps {
  open?: boolean;
  sections: {
    label: string;
    href?: string;
    items?: {
      label: string;
      href?: string;
    }[];
  }[];
}

const MenuBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ theme, open }) => ({
  width: '100vw',
  height: open ? `calc(100svh - ${theme.layout.headerHeight})` : 0,
  overflow: 'scroll',
  overscrollBehavior: 'contain',
  flexShrink: 0,
  marginBottom: 5,
  transition: `height ${theme.transitions.duration.short}ms ${theme.transitions.easing.sharp}`,
  backgroundColor: theme.vars.palette.background.paper,
  [`& .MuiDrawer-paper`]: {
    borderRadius: 0,
  },
  position: 'absolute',
  zIndex: 20,
}));

const MenuSection = ({ label, href, items }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleClick = (e) => {
    if (items) {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  return (
    <>
      <ListItemButton onClick={handleClick} sx={{ px: 2 }} href={href}>
        <ListItemText primary={<Typography variant="h3">{label}</Typography>} />
        {items && (isOpen ? <CaretUp size={16} /> : <CaretDown size={16} />)}
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={({ vars }) => ({ backgroundColor: vars.palette.background.default })}>
          {items &&
            items.map(({ label, href }) => {
              if (label === 'PLACEHOLDER') {
                return null;
              }

              if (!href) {
                return (
                  <ListSubheader key={label} sx={{ position: 'relative', pl: 3 }}>
                    <Typography variant="h5" color="text.secondary">
                      {label}
                    </Typography>
                  </ListSubheader>
                );
              }

              const isExternal = href.startsWith('http');

              return (
                <ListItemButton
                  key={label}
                  sx={({ vars }) => ({ pl: 4, zIndex: 1, backgroundColor: vars.palette.background.default })}
                  href={href}
                >
                  <ListItemText primary={<Typography variant="h4">{label}</Typography>} />
                  {isExternal && <ArrowSquareOut size={16} style={{ marginRight: '4px' }} />}
                </ListItemButton>
              );
            })}
        </List>
      </Collapse>
    </>
  );
};

export const HeaderMenuLinksMobile = ({ sections, open }: HeaderMenuLinksMobileProps) => {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'initial';
    }
    return () => {
      document.body.style.overflow = 'initial';
    };
  }, [open]);

  return (
    <MenuBox open={open}>
      <List
        sx={({ vars }) => ({
          width: '100%',
          height: '100%',
          overscrollBehavior: 'contain',
          backgroundColor: vars.palette.background.default,
        })}
        component="nav"
      >
        {sections.map(({ label, href, items }) => (
          <MenuSection key={label} href={href} label={label} items={items} />
        ))}
      </List>
    </MenuBox>
  );
};
